import { useEffect, useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BORNES_BULLE } from '@nextilt/commun';
import { refusDeCreation } from '../partage/creation';
import { saisieDuProjetDemandee } from '../partage/saisieDuProjet';
import { useCreer } from '../partage/useArbre';
import {
  ANNULER,
  CREER,
  EXEMPLE_DE_PROJET,
  IMPORTER_UN_PLAN_D_IA,
  NOM_DU_NOUVEAU_PROJET,
  NOUVEAU_PROJET,
  PARTIR_D_UN_MODELE,
} from '../partage/vocabulaire';
import { IconeFeuilleCornee, IconeModele, IconePlus } from '../partage/icones';
import { ImporterPlan } from '../import-ia';
import { Modele } from '../modeles';
import './Dossiers.css';

/**
 * Les trois chemins d'un nouveau projet, à la place du bloc en pointillé (chantier
 * `revue-design`, ticket 05). « + Nouveau projet » ouvre la saisie du nom juste dessous ; le
 * tuto entoure ce bouton tant que la saisie est fermée, puis le champ.
 *
 * Sortis de `Dossiers` au chantier `telephone-projets-fiche`, ticket 01 ; l'ordinateur seul les
 * montre depuis le ticket 05 : au téléphone, le + de la barre du bas les reprend (`Capture.tsx`).
 * Depuis le chantier etagere-des-projets, dessinés comme la maquette du carnet `projets.html`
 * (`.actions-p`) : trois boutons à leur taille, chacun avec son icône, le premier foncé.
 */
export function NouveauProjet() {
  const creer = useCreer(null);
  const [saisie, setSaisie] = useState(false);
  const [nom, setNom] = useState('');
  const [modeleOuvert, setModeleOuvert] = useState(false);
  const [importOuvert, setImportOuvert] = useState(false);

  /*
   * « Nouveau projet » de la barre de gauche (chantier `navigation`, 01) arrive ici avec un état
   * qui ouvre la saisie ; l'état est retiré aussitôt, pour qu'un rechargement ne la rouvre pas.
   */
  const ici = useLocation();
  const naviguer = useNavigate();
  useEffect(() => {
    if (!saisieDuProjetDemandee(ici.state)) return;
    setSaisie(true);
    naviguer(ici.pathname + ici.search, { replace: true, state: null });
  }, [ici.state, ici.pathname, ici.search, naviguer]);

  function fermerLaSaisie() {
    setSaisie(false);
    setNom('');
  }

  /** Fermer de la main : le refus d'avant ne se montrera plus à la prochaine ouverture. */
  function annuler() {
    creer.reset();
    fermerLaSaisie();
  }

  function ouvrir(evenement: FormEvent) {
    evenement.preventDefault();
    const propre = nom.trim();
    if (!propre) return;
    // Sans réseau, le projet ne part pas (spec `telephone`, « Sans réseau ») : la saisie se
    // rouvre avec le nom tapé, et le refus le dit.
    creer.mutate(propre, {
      onError: (erreur) => {
        if (!refusDeCreation(erreur)) return;
        setNom(propre);
        setSaisie(true);
      },
    });
    fermerLaSaisie();
  }

  const refus = refusDeCreation(creer.error);

  return (
    <>
      <div className="dossiers-nouveau" data-tuto="dossier-nouveau">
        <div className="dossiers-chemins">
          <button
            type="button"
            className="dossiers-chemin dossiers-chemin--principal"
            data-tuto={saisie ? undefined : 'champ-nouveau-dossier'}
            aria-expanded={saisie}
            onClick={() => (saisie ? annuler() : setSaisie(true))}
          >
            <IconePlus className="dossiers-chemin-icone" />
            <span>{NOUVEAU_PROJET}</span>
          </button>
          <button type="button" className="dossiers-chemin" onClick={() => setModeleOuvert(true)}>
            <IconeModele className="dossiers-chemin-icone" />
            {PARTIR_D_UN_MODELE}
          </button>
          <button type="button" className="dossiers-chemin" onClick={() => setImportOuvert(true)}>
            <IconeFeuilleCornee className="dossiers-chemin-icone" />
            {IMPORTER_UN_PLAN_D_IA}
          </button>
        </div>

        {saisie && (
          <form className="dossiers-saisie" onSubmit={ouvrir}>
            <input
              className="champ dossiers-champ"
              data-tuto="champ-nouveau-dossier"
              value={nom}
              onChange={(e) => setNom(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') annuler();
              }}
              placeholder={EXEMPLE_DE_PROJET}
              aria-label={NOM_DU_NOUVEAU_PROJET}
              maxLength={BORNES_BULLE.titreMax}
              autoFocus
            />
            <button type="submit" className="bouton dossiers-creer" disabled={!nom.trim()}>
              {CREER}
            </button>
            <button type="button" className="bouton bouton-discret dossiers-annuler" onClick={annuler}>
              {ANNULER}
            </button>
          </form>
        )}
        {saisie && refus && (
          <p className="refus refus-sous-le-champ" role="status">
            {refus}
          </p>
        )}
      </div>

      {modeleOuvert && <Modele onFermer={() => setModeleOuvert(false)} />}
      {importOuvert && <ImporterPlan onFermer={() => setImportOuvert(false)} />}
    </>
  );
}
